import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import SEO from '../components/SEO';

const EVENT_TYPES = ['conference', 'workshop', 'webinar', 'meeting', 'symposium', 'other'];

const EventCalendar = () => {
  const { t } = useTranslation();
  const { token, isAdmin, API } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [formData, setFormData] = useState({
    title: '', description: '', location: '', start_date: '', end_date: '', event_type: 'conference', is_published: true,
  });

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/events`);
      setEvents(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const headers = { Authorization: `Bearer ${token}` };
    try {
      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent.id}`, formData, { headers });
      } else {
        await axios.post(`${API}/events`, formData, { headers });
      }
      setShowForm(false);
      setEditingEvent(null);
      setFormData({ title: '', description: '', location: '', start_date: '', end_date: '', event_type: 'conference', is_published: true });
      fetchEvents();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to save event'); }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await axios.delete(`${API}/events/${eventId}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchEvents();
    } catch (err) { alert('Failed to delete'); }
  };

  const startEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title, description: event.description, location: event.location || '',
      start_date: event.start_date, end_date: event.end_date || '', event_type: event.event_type, is_published: event.is_published,
    });
    setShowForm(true);
  };

  const getDaysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const getFirstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();

  const getEventsForDate = (day) => {
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => e.start_date && e.start_date.startsWith(dateStr));
  };

  const prevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  const nextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));

  if (loading) return <div style={{ textAlign: 'center', padding: '3rem' }}>{t('common.loading')}</div>;

  const upcomingEvents = events.filter(e => new Date(e.start_date) >= new Date()).sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
  const pastEvents = events.filter(e => new Date(e.start_date) < new Date()).sort((a, b) => new Date(b.start_date) - new Date(a.start_date));

  return (
    <div data-testid="events-page">
      <SEO path="/events" title="Events & Calendar" description="Upcoming conferences, workshops, webinars, and community health events organized by AFHOS across Africa." />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1>{t('events.title')}</h1>
          <p style={{ color: 'var(--muted)' }}>{t('events.subtitle')}</p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className={`btn btn-small ${viewMode === 'list' ? '' : 'btn-secondary'}`} onClick={() => setViewMode('list')} data-testid="view-list">{t('events.list')}</button>
          <button className={`btn btn-small ${viewMode === 'calendar' ? '' : 'btn-secondary'}`} onClick={() => setViewMode('calendar')} data-testid="view-calendar">{t('events.calendar')}</button>
          {isAdmin && (
            <button className="btn btn-small" onClick={() => { setShowForm(!showForm); setEditingEvent(null); setFormData({ title: '', description: '', location: '', start_date: '', end_date: '', event_type: 'conference', is_published: true }); }} data-testid="new-event-btn">
              {showForm ? t('common.cancel') : '+ ' + t('events.newEvent')}
            </button>
          )}
        </div>
      </div>

      {showForm && isAdmin && (
        <form onSubmit={handleSubmit} className="card" style={{ padding: '1.5rem', marginBottom: '2rem' }} data-testid="event-form">
          <h3 style={{ marginBottom: '1rem', color: 'var(--brand)' }}>{editingEvent ? t('events.editEvent') : t('events.newEvent')}</h3>
          <div className="form-group"><label>{t('events.eventTitle')} *</label><input value={formData.title} onChange={e => setFormData(p => ({...p, title: e.target.value}))} required data-testid="event-title-input" /></div>
          <div className="form-row">
            <div className="form-group"><label>{t('events.startDate')} *</label><input type="date" value={formData.start_date} onChange={e => setFormData(p => ({...p, start_date: e.target.value}))} required data-testid="event-start-date" /></div>
            <div className="form-group"><label>{t('events.endDate')}</label><input type="date" value={formData.end_date} onChange={e => setFormData(p => ({...p, end_date: e.target.value}))} data-testid="event-end-date" /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>{t('events.location')}</label><input value={formData.location} onChange={e => setFormData(p => ({...p, location: e.target.value}))} data-testid="event-location-input" /></div>
            <div className="form-group">
              <label>{t('events.type')}</label>
              <select value={formData.event_type} onChange={e => setFormData(p => ({...p, event_type: e.target.value}))} data-testid="event-type-select">
                {EVENT_TYPES.map(type => <option key={type} value={type}>{t(`events.${type}`)}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group"><label>{t('events.description')} *</label><textarea value={formData.description} onChange={e => setFormData(p => ({...p, description: e.target.value}))} required rows="4" data-testid="event-description-input" /></div>
          <button type="submit" className="btn" data-testid="event-submit-btn">{editingEvent ? t('events.editEvent') : t('events.createEvent')}</button>
        </form>
      )}

      {viewMode === 'calendar' && (
        <div className="calendar-view" data-testid="calendar-view">
          <div className="calendar-header">
            <button className="btn btn-small btn-secondary" onClick={prevMonth}>&laquo;</button>
            <h3>{currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h3>
            <button className="btn btn-small btn-secondary" onClick={nextMonth}>&raquo;</button>
          </div>
          <div className="calendar-grid">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => <div key={d} className="calendar-day-header">{d}</div>)}
            {Array.from({ length: getFirstDayOfMonth(currentMonth) }, (_, i) => <div key={`empty-${i}`} className="calendar-cell empty" />)}
            {Array.from({ length: getDaysInMonth(currentMonth) }, (_, i) => {
              const day = i + 1;
              const dayEvents = getEventsForDate(day);
              const isToday = new Date().getDate() === day && new Date().getMonth() === currentMonth.getMonth() && new Date().getFullYear() === currentMonth.getFullYear();
              return (
                <div key={day} className={`calendar-cell ${isToday ? 'today' : ''} ${dayEvents.length ? 'has-events' : ''}`}>
                  <span className="calendar-day-num">{day}</span>
                  {dayEvents.map(ev => <div key={ev.id} className="calendar-event-dot" title={ev.title}>{ev.title.substring(0, 12)}</div>)}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {viewMode === 'list' && (
        <>
          {upcomingEvents.length > 0 && (
            <div data-testid="upcoming-events">
              <h3 style={{ marginBottom: '1rem', color: 'var(--brand)' }}>{t('events.upcoming')}</h3>
              {upcomingEvents.map(event => (
                <EventCard key={event.id} event={event} isAdmin={isAdmin} onEdit={startEdit} onDelete={handleDelete} t={t} />
              ))}
            </div>
          )}
          {pastEvents.length > 0 && (
            <div data-testid="past-events" style={{ marginTop: '2rem' }}>
              <h3 style={{ marginBottom: '1rem', color: 'var(--muted)' }}>{t('events.past')}</h3>
              {pastEvents.map(event => (
                <EventCard key={event.id} event={event} isAdmin={isAdmin} onEdit={startEdit} onDelete={handleDelete} isPast t={t} />
              ))}
            </div>
          )}
          {events.length === 0 && (
            <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
              <p style={{ color: 'var(--muted)' }}>{t('events.noEvents')}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const EventCard = ({ event, isAdmin, onEdit, onDelete, isPast, t }) => (
  <div className={`card event-card ${isPast ? 'past' : ''}`} data-testid={`event-card-${event.id}`} style={{ padding: '1.5rem', marginBottom: '1rem' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
          <span className="badge badge-type">{t(`events.${event.event_type}`)}</span>
          <h3 style={{ margin: 0 }}>{event.title}</h3>
        </div>
        <p style={{ color: 'var(--muted)', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
          {new Date(event.start_date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          {event.end_date && ` - ${new Date(event.end_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`}
          {event.location && ` | ${event.location}`}
        </p>
        <p style={{ lineHeight: '1.6' }}>{event.description}</p>
      </div>
      {isAdmin && (
        <div style={{ display: 'flex', gap: '0.5rem', marginLeft: '1rem' }}>
          <button className="btn btn-small btn-secondary" onClick={() => onEdit(event)} data-testid={`edit-event-${event.id}`}>{t('common.edit')}</button>
          <button className="btn btn-small btn-danger" onClick={() => onDelete(event.id)} data-testid={`delete-event-${event.id}`}>{t('common.delete')}</button>
        </div>
      )}
    </div>
  </div>
);

export default EventCalendar;
